import { DatabaseViewRefBlockSchema } from '@blocksuite/affine-model';
import {
  BlockHtmlAdapterExtension,
  type BlockHtmlAdapterMatcher,
  BlockMarkdownAdapterExtension,
  type BlockMarkdownAdapterMatcher,
  BlockPlainTextAdapterExtension,
  type BlockPlainTextAdapterMatcher,
} from '@blocksuite/affine-shared/adapters';

const FLAVOUR = DatabaseViewRefBlockSchema.model.flavour;

interface ViewRefProps {
  sourceDocId?: string;
  sourceBlockId?: string;
  viewId?: string;
}

/**
 * A view ref has no content of its own — it is exported as a link back to
 * the source database view.
 */
const refUrl = (props: ViewRefProps) => {
  const docId = props.sourceDocId ?? '';
  const params = new URLSearchParams();
  if (props.sourceBlockId) {
    params.set('blockIds', props.sourceBlockId);
  }
  if (props.viewId) {
    params.set('viewId', props.viewId);
  }
  const query = params.toString();
  return `affine://${docId}${query ? `?${query}` : ''}`;
};

const refLabel = (props: ViewRefProps) =>
  `${props.sourceBlockId ?? 'database'} / ${props.viewId ?? 'default view'}`;

export const databaseViewRefBlockPlainTextAdapterMatcher: BlockPlainTextAdapterMatcher =
  {
    flavour: FLAVOUR,
    toMatch: () => false,
    fromMatch: o => o.node.flavour === FLAVOUR,
    toBlockSnapshot: {},
    fromBlockSnapshot: {
      enter: (o, context) => {
        const props = o.node.props as ViewRefProps;
        context.textBuffer.content += `${refLabel(props)}: ${refUrl(props)}\n`;
        context.walkerContext.skipAllChildren();
      },
    },
  };

export const databaseViewRefBlockMarkdownAdapterMatcher: BlockMarkdownAdapterMatcher =
  {
    flavour: FLAVOUR,
    toMatch: () => false,
    fromMatch: o => o.node.flavour === FLAVOUR,
    toBlockSnapshot: {},
    fromBlockSnapshot: {
      enter: (o, context) => {
        const props = o.node.props as ViewRefProps;
        context.walkerContext
          .openNode(
            {
              type: 'paragraph',
              children: [
                {
                  type: 'link',
                  url: refUrl(props),
                  children: [{ type: 'text', value: refLabel(props) }],
                },
              ],
            },
            'children'
          )
          .closeNode();
        context.walkerContext.skipAllChildren();
      },
    },
  };

export const databaseViewRefBlockHtmlAdapterMatcher: BlockHtmlAdapterMatcher = {
  flavour: FLAVOUR,
  toMatch: () => false,
  fromMatch: o => o.node.flavour === FLAVOUR,
  toBlockSnapshot: {},
  fromBlockSnapshot: {
    enter: (o, context) => {
      const props = o.node.props as ViewRefProps;
      context.walkerContext
        .openNode(
          {
            type: 'element',
            tagName: 'a',
            properties: { href: refUrl(props) },
            children: [{ type: 'text', value: refLabel(props) }],
          },
          'children'
        )
        .closeNode();
      context.walkerContext.skipAllChildren();
    },
  },
};

export const DatabaseViewRefBlockPlainTextAdapterExtension =
  BlockPlainTextAdapterExtension(databaseViewRefBlockPlainTextAdapterMatcher);

export const DatabaseViewRefBlockMarkdownAdapterExtension =
  BlockMarkdownAdapterExtension(databaseViewRefBlockMarkdownAdapterMatcher);

export const DatabaseViewRefBlockHtmlAdapterExtension =
  BlockHtmlAdapterExtension(databaseViewRefBlockHtmlAdapterMatcher);
